import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ItineraryService } from 'src/app/business-service/quote/itinerary.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { ModalsService } from 'src/app/modules/shared/services/modals/modals.service';

@Component({
  selector: 'app-template-data',
  templateUrl: './template-data.component.html',
  styleUrls: ['./template-data.component.scss'],
})
export class TemplateDataComponent  implements OnInit {

  @Input() templateId:any;
  @Input() leadId:any;
  @Input() itineraryId:any;
  @Input() storedFormData:any;
  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() openReviewScreen: EventEmitter<any> = new EventEmitter<any>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter<any>();

  templateDetailsForm!: FormGroup;

  constructor(
    private _fb:FormBuilder,
    private _globalService: GlobalService,
    private _itineraryService: ItineraryService,
    private _modalService: ModalsService
  ) { }

  ngOnInit() {    
    this.templateDetailsForm = this._fb.group({
      leadId: this.leadId,
      quoteId: this.itineraryId,
      templateId: this.storedFormData.templateId || this.templateId,
      templateName: ["", [Validators.required]],
      templateTitle: ["", [Validators.required]],
      image1: [null],
      color1: ["", Validators.required],
      color2: ["", Validators.required],
      enquiryNumber1: [{countryCode: '+91', phone: '' }, Validators.required],
      enquiryNumber2: [{countryCode: '+91', phone: '' }, Validators.required]
    })

    this.templateDetailsForm.valueChanges.subscribe(()=>{
      this.templateDetailsForm.markAsTouched();
    })

    if(this.storedFormData){
      this.patchTemplateFormData(this.storedFormData);
    }
  }

  patchTemplateFormData(formData:any){
    this.templateDetailsForm.patchValue({
      templateId: formData.baseTemplateId,
      templateName: formData.name,
      templateTitle: formData.title,
      color1: formData.primaryColor,
      color2: formData.secondaryColor,
      enquiryNumber1: {countryCode: '+91', phone: formData.enquiryNumber[0]? formData.enquiryNumber[0] : '' },
      enquiryNumber2: {countryCode: '+91', phone: formData.enquiryNumber[1]? formData.enquiryNumber[1] : '' },
      image1: formData.image1.path
    })

    if(!formData.secondaryColor){
      this.templateDetailsForm.get('color2')?.disable();
    }
  }

  onFileSelected(event: any, controlName: string) {
    const file = event.target.files[0];
    // Checking if image is more than 10MB
    if (file.size > 10 * 1024 * 1024) {
      this._globalService.customAlert("File size should be less than 10MB", "Invalid File Size");
      return;
    }
    if (file) {
      this.templateDetailsForm.get(controlName)?.setValue(file);
      this.templateDetailsForm.markAsDirty();
      this.templateDetailsForm.markAsTouched();
    }
  }
  
  openImage(file: any) {    
    if(file instanceof File) {
      const fileURL = URL.createObjectURL(file);
      this._modalService.openImageModal(fileURL)
    }else{
      this._modalService.openImageModal(file)
    }
  }

  getImgUrl(file:any){
    if(file instanceof File) {
      const fileURL = URL.createObjectURL(file);
      return fileURL;
    }else{
      return file;
    }
  }


  async onSubmit(): Promise<boolean> {
    if (!this.templateDetailsForm.valid) {
      this.templateDetailsForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._globalService.customAlert("Fill all required fields to continue", "Form is invalid");
      return false; // Form is invalid
    }
  
    try {
      const enquiryNumber1 = this.templateDetailsForm.get('enquiryNumber1')?.value;
      const enquiryNumber2 = this.templateDetailsForm.get('enquiryNumber2')?.value;
  
      // Create a new FormData object
      const formData = new FormData();
  
      // Append non-file fields
      formData.append('leadId', this.templateDetailsForm.get('leadId')?.value);
      formData.append('quoteId', this.templateDetailsForm.get('quoteId')?.value);
      formData.append('baseTemplateId', this.templateDetailsForm.get('templateId')?.value);
      formData.append('name', this.templateDetailsForm.get('templateName')?.value);
      formData.append('title', this.templateDetailsForm.get('templateTitle')?.value);
      formData.append('primaryColor', this.templateDetailsForm.get('color1')?.value);
      formData.append('secondaryColor', this.templateDetailsForm.get('color2')?.value);
      formData.append('enquiryNumber1', enquiryNumber1.phone);
      formData.append('enquiryNumber2', enquiryNumber2.phone);
      formData.append('id', this.storedFormData.id);
      formData.append('organizationId', this.storedFormData.organizationId);
  
      // Append file fields if they are selected
      const imageFields = ['image1'];
      for (const field of imageFields) {
        const image = this.templateDetailsForm.get(field)?.value;
        if (image instanceof File) {
          formData.append(field, image);
        }
      }
  
      // Update quote template details
      const data = await this._itineraryService
        .updateQuoteTemplateDetails(this.templateDetailsForm.get('quoteId')?.value, formData)
        .toPromise();  
      data.dataSaved = true;
      this.storeFormData.emit(data);
      this.templateDetailsForm.markAsPristine();
  
      return true; // Form submitted successfully
    } catch (error) {
      console.error("Error while updating quote template details:", error);
      return false; // Submission failed
    }
  }
  

  async resetForm(){
    if(await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")){
      this.ngOnInit();
    }    
  }

  goPrevious(){
    this.viewChange.emit(true);
  }

  goNext(){
    this.openReviewScreen.emit();
  }

  async saveAndNext(){
    if(await this.onSubmit()){
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return (this.templateDetailsForm.dirty || this.templateDetailsForm.invalid) && this.templateDetailsForm.touched;
  }


}
